import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Light/dark theme for the whole app.
// - The chosen mode ("light" | "dark" | "system") is saved per browser.
// - The applied theme is written to <html data-theme="..."> (our design
//   tokens in styles/tokens.css switch on it) and data-bs-theme (Bootstrap
//   5.3 components follow it).
// - public/index.html applies the saved theme before React loads, so there
//   is no flash of the wrong theme.

export const THEME_STORAGE_KEY = "evs-theme";
export const THEME_MODES = ["light", "dark", "system"];

const readSavedMode = () => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return THEME_MODES.includes(saved) ? saved : "light";
  } catch (e) {
    return "light";
  }
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const resolveTheme = (mode) => (mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode);

export const applyTheme = (theme) => {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.setAttribute("data-bs-theme", theme);
};

const ThemeContext = createContext({
  mode: "light",
  theme: "light",
  setMode: () => {},
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readSavedMode);
  const [theme, setTheme] = useState(() => resolveTheme(readSavedMode()));

  const setMode = useCallback((nextMode) => {
    if (!THEME_MODES.includes(nextMode)) return;
    setModeState(nextMode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, nextMode);
    } catch (e) {
      // Storage unavailable (private mode): the choice lasts for this visit
    }
  }, []);

  // Apply whenever the mode changes; follow the OS setting in "system" mode
  useEffect(() => {
    const update = () => {
      const next = resolveTheme(mode);
      setTheme(next);
      applyTheme(next);
    };
    update();
    if (mode !== "system" || !window.matchMedia) return undefined;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [mode]);

  const toggleTheme = useCallback(() => setMode(theme === "dark" ? "light" : "dark"), [theme, setMode]);

  const value = useMemo(() => ({ mode, theme, setMode, toggleTheme }), [mode, theme, setMode, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
